import { useAuthContext } from '@/contexts/auth-context';
import { SAN_FRANCISCO_HUB_PATH_IDENTIFIER } from '@/lib/constants';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { REDIRECT_AFTER_SIGNIN_KEY } from './index';

export const Route = createFileRoute('/auth/complete')({
  component: AuthComplete,
});

// Final step of the Google Identity Services redirect flow: the backend
// just 302'd us here with `#access_token=<jwt>` in the URL fragment. Parse
// it, seed the auth context, then forward to wherever the user was originally
// trying to go (stashed in sessionStorage on the way out).
function AuthComplete() {
  const auth = useAuthContext();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const redirectTarget = sessionStorage.getItem(REDIRECT_AFTER_SIGNIN_KEY);
    sessionStorage.removeItem(REDIRECT_AFTER_SIGNIN_KEY);

    // Wipe the token out of the URL so it doesn't sit in history.
    window.history.replaceState(null, '', window.location.pathname);

    const run = async () => {
      if (!accessToken) {
        navigate({ to: '/auth', search: { error: 'missing_token' } });
        return;
      }
      const ok = await auth.completeSignIn(accessToken);
      if (!ok) {
        navigate({ to: '/auth', search: { error: 'signin_failed' } });
        return;
      }

      if (redirectTarget) {
        window.location.replace(redirectTarget);
        return;
      }

      navigate({
        to: '/$hub/all',
        params: {
          hub: auth.user?.current_hub ?? SAN_FRANCISCO_HUB_PATH_IDENTIFIER,
        },
      });
    };
    run();
  }, [auth, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="text-sm text-gray-600 dark:text-gray-300">
        Signing you in…
      </div>
    </div>
  );
}
